import './styles.scss'
import image from '../../../Assets/Images/quote-modal-img.jpeg'
import { CSSTransition } from 'react-transition-group'
import { useEffect, useRef, useState } from 'react'
import Input from '../../Input'
import InputArea from '../../InputArea'
import Select from '../../Select'
import CustomButton from '../../CustomButton'
import { useForm } from '../../../Hooks/useForm'
import emailjs from '@emailjs/browser'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

const templateId = process.env.REACT_APP_EMAILJS_QUOTE_TEMPLATE
const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID
const publicId = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const validate = (form) => {
  const errs = {}
  if (!form.name.trim()) errs.name = 'Name is required'
  if (!form.email.trim()) errs.email = 'Email is required'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    errs.email = 'Enter a valid email'
  if (!form.origin.trim()) errs.origin = 'Origin is required'
  if (!form.destination.trim()) errs.destination = 'Destination is required'
  return errs
}

const QuoteForm = ({ showForm, handleClose, ...props }) => {
  const formRef = useRef(null)
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState({})
  const [submitError, setSubmitError] = useState(false)

  const handleClick = (e) => e?.stopPropagation()

  const { form, reset, onChange } = useForm({
    name: '',
    email: '',
    phone: '',
    origin: '',
    destination: '',
    cargoType: '',
    weight: '',
    date: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => {
    onChange(e.target.name, e.target.value)
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: undefined }))
    }
  }

  const isFormValid = Boolean(
    form.name.trim() && form.email.trim() && form.origin.trim() && form.destination.trim()
  )

  useEffect(() => {
    return reset
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    const errs = validate(form)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }

    onChange('loading', true)
    setSubmitError(false)

    try {
      await emailjs.send(serviceId, templateId, form, publicId)
      reset()
      setErrors({})
      setSubmitted(true)
    } catch (error) {
      setSubmitError(true)
    }

    onChange('loading', false)
  }

  const handleReset = () => {
    setSubmitted(false)
    setErrors({})
    setSubmitError(false)
  }

  return (
    <CSSTransition
      in={showForm}
      nodeRef={formRef}
      timeout={300}
      unmountOnExit
      classNames="quote-form"
      onExited={handleReset}
    >
      <div
        ref={formRef}
        onClick={handleClick}
        className="quote-form"
        {...props}
      >
        <div className="quote-form__image">
          <img src={image} alt="" />
        </div>

        <div className="quote-form__content">
          {submitted ? (
            <div className="quote-form__success">
              <FontAwesomeIcon className="success-icon" icon={solid('circle-check')} />
              <h4>Quote Request Sent!</h4>
              <p>We'll review your details and get back to you with pricing shortly.</p>
              <div className="success-actions">
                <button className="btn-another" onClick={handleReset}>
                  Submit another
                </button>
                <button className="btn-close" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h5 className="title">Get A Free Quote</h5>

              {submitError && (
                <div className="quote-form__error-banner">
                  <FontAwesomeIcon icon={solid('circle-exclamation')} />
                  Something went wrong. Please try again.
                </div>
              )}

              <div className="form-grid">
                <Input
                  title="Full Name"
                  required
                  placeholder="John Doe"
                  value={form.name}
                  onChange={handleChange}
                  name="name"
                  error={errors.name}
                />
                <Input
                  title="Email Address"
                  type="email"
                  required
                  placeholder="john@example.com"
                  value={form.email}
                  onChange={handleChange}
                  name="email"
                  error={errors.email}
                />
                <Input
                  title="Origin"
                  required
                  placeholder="City, Country"
                  value={form.origin}
                  onChange={handleChange}
                  name="origin"
                  error={errors.origin}
                />
                <Input
                  title="Destination"
                  required
                  placeholder="City, Country"
                  value={form.destination}
                  onChange={handleChange}
                  name="destination"
                  error={errors.destination}
                />
                <Select
                  title="Cargo Type"
                  placeholder="Select cargo type..."
                  value={form.cargoType}
                  onChange={handleChange}
                  name="cargoType"
                  options={[
                    { value: 'general', label: 'General Goods' },
                    { value: 'perishables', label: 'Perishables / Food' },
                    { value: 'electronics', label: 'Electronics' },
                    { value: 'pharmaceuticals', label: 'Pharmaceuticals' },
                    { value: 'hazmat', label: 'Hazardous Materials' },
                    { value: 'oversized', label: 'Oversized / Heavy Cargo' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
                <Select
                  title="Estimated Weight"
                  placeholder="Select weight range..."
                  value={form.weight}
                  onChange={handleChange}
                  name="weight"
                  options={[
                    { value: 'under-50', label: 'Under 50 kg' },
                    { value: '50-200', label: '50 – 200 kg' },
                    { value: '200-500', label: '200 – 500 kg' },
                    { value: '500-1000', label: '500 kg – 1 ton' },
                    { value: '1-5tons', label: '1 – 5 tons' },
                    { value: '5tons+', label: '5+ tons' },
                  ]}
                />
                <Input
                  title="Phone Number"
                  placeholder="+1 (555) 000-0000"
                  value={form.phone}
                  onChange={handleChange}
                  name="phone"
                />
                <Input
                  title="Preferred Pickup Date"
                  type="date"
                  value={form.date}
                  onChange={handleChange}
                  name="date"
                />
                <div className="full-col">
                  <InputArea
                    title="Additional Notes"
                    rows={2}
                    maxLength={300}
                    placeholder="Special requirements or handling instructions..."
                    value={form.msg}
                    onChange={handleChange}
                    name="msg"
                  />
                </div>
              </div>

              <div className="quote-form__footer">
                <span>
                  <FontAwesomeIcon icon={solid('clock')} />
                  Typically respond within 1 business day
                </span>
                <CustomButton
                  loading={form.loading}
                  disabled={!isFormValid || form.loading}
                  otherClassName="submit"
                  type="submit"
                  label="Request Quote"
                />
              </div>
            </form>
          )}
        </div>
      </div>
    </CSSTransition>
  )
}

export default QuoteForm
